
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useMechanicShare } from '@/contexts/MechanicShareContext';
import { Car } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import MediaUploader from '@/components/MediaUploader';
import { toast } from 'sonner';

interface MechanicSymptomFormProps {
  token: string;
  car: Car;
  onSuccess: () => void;
}

interface FormValues {
  title: string;
  description: string;
  date: string;
  mechanicName: string;
  garageName: string;
}

const MechanicSymptomForm: React.FC<MechanicSymptomFormProps> = ({ token, car, onSuccess }) => {
  const { logMechanicSymptom } = useMechanicShare();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  
  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      mechanicName: '',
      garageName: '',
    }
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    
    try {
      const symptomId = logMechanicSymptom(token, {
        title: values.title,
        description: values.description,
        date: values.date,
        mechanicName: values.mechanicName,
        garageName: values.garageName || undefined,
        images: uploadedImages.length > 0 ? uploadedImages : undefined,
      });
      
      if (symptomId) {
        toast.success('Symptom added successfully');
        onSuccess();
      } else {
        toast.error('Failed to add symptom');
      }
    } catch (error) {
      console.error('Error adding symptom:', error);
      toast.error('An error occurred while saving');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImagesUpload = (urls: string[]) => {
    setUploadedImages(urls);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          rules={{ required: 'Title is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Issue Title*</FormLabel>
              <FormControl>
                <Input placeholder="Strange noise, Check engine light, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          rules={{ required: 'Description is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description*</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe the issue in detail..." 
                  {...field} 
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date"
          rules={{ required: 'Date is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date*</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mechanicName"
          rules={{ required: 'Your name is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your Name*</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="garageName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Garage/Shop Name</FormLabel>
              <FormControl>
                <Input placeholder="ABC Auto Repair" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="border rounded-md p-3">
          <FormLabel className="block mb-2">Add Images (Optional)</FormLabel>
          <MediaUploader
            onUpload={handleImagesUpload}
            maxFiles={3}
            allowVideo={false}
            showPreview
          />
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-lime hover:bg-lime/90 text-black"
          >
            {isLoading ? 'Saving...' : 'Log Issue'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default MechanicSymptomForm;
